// ============================================================================
// Payment Service
// Fetches payment data from the admin-backend API (proxied from hcp-payment)
// ============================================================================

import { fetchWithAuth } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

// ============================================================================
// Types (matching hcp-payment PaymentRecord schema)
// ============================================================================

export interface PaymentRecord {
  id: string;
  store_id: number;
  reservation_id?: string;
  customer_id?: string;
  amount: number;
  currency: string;
  payment_type: string;
  flow_type?: string;
  status: string;
  gateway_provider: string;
  gateway_environment?: string;
  transaction_id?: string;
  payment_method?: string;
  last_four_digits?: string;
  card_brand?: string;
  is_off_session: boolean;
  initiated_at?: string;
  processed_at?: string;
  confirmed_at?: string;
  created_at?: string;
  last_error_code?: string;
  last_error_message?: string;
}

interface PaymentListResponse {
  payments: PaymentRecord[];
  total: number;
  page: number;
  page_size: number;
}

export interface PaymentStatusUpdateResponse {
  success: boolean;
  payment_id: string;
  old_status: string;
  new_status: string;
  no_op: boolean;
  updated_at: string;
  message: string | null;
}

// ============================================================================
// API Functions
// ============================================================================

/** The API serializes decimal amounts as strings; coerce to number so
 *  arithmetic (e.g. summing payments) doesn't concatenate. */
function normalizePayment(p: PaymentRecord): PaymentRecord {
  return { ...p, amount: Number(p.amount) };
}

export async function fetchPaymentsForReservation(
  reservationId: number,
  storeId: number
): Promise<PaymentRecord[]> {
  const params = new URLSearchParams({
    store_id: storeId.toString(),
    reservation_id: reservationId.toString(),
    page_size: '200',
  });

  const response: PaymentListResponse = await fetchWithAuth(
    `${API_BASE_URL}/payments?${params.toString()}`
  );

  return response.payments.map(normalizePayment);
}

/**
 * Fetch all pending manual (cash/check/card_terminal) payments for a store.
 * Used to flag reservations with payment due (e.g. daily report badge).
 */
export async function fetchPendingManualPayments(storeId: number): Promise<PaymentRecord[]> {
  const params = new URLSearchParams({
    store_id: storeId.toString(),
    payment_status: 'pending',
    gateway_provider: 'manual',
    page_size: '200',
  });

  const response: PaymentListResponse = await fetchWithAuth(
    `${API_BASE_URL}/payments?${params.toString()}`
  );

  return response.payments.map(normalizePayment);
}

export interface FetchPaymentsParams {
  storeId: number;
  paymentStatus?: string;
  gatewayProvider?: string;
  /** Safety cap on total rows fetched across pages (default 2000). */
  maxRows?: number;
}

/**
 * Fetch payments for a store with optional filters, paging through the
 * full result set. Used by the Analysis dashboard, which aggregates
 * client-side (see Documents/202607-ANALYSIS_DASHBOARD_DESIGN.md).
 */
export async function fetchPayments(params: FetchPaymentsParams): Promise<PaymentRecord[]> {
  const pageSize = 200;
  const maxRows = params.maxRows ?? 2000;
  const all: PaymentRecord[] = [];
  let page = 1;

  while (all.length < maxRows) {
    const searchParams = new URLSearchParams({
      store_id: params.storeId.toString(),
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (params.paymentStatus) searchParams.append('payment_status', params.paymentStatus);
    if (params.gatewayProvider) searchParams.append('gateway_provider', params.gatewayProvider);

    const response: PaymentListResponse = await fetchWithAuth(
      `${API_BASE_URL}/payments?${searchParams.toString()}`
    );
    all.push(...response.payments);
    if (all.length >= response.total || response.payments.length < pageSize) break;
    page += 1;
  }

  return all.slice(0, maxRows).map(normalizePayment);
}

/**
 * Manually correct the status of a manual (cash/check/card_terminal) payment.
 * Allowed transitions: pending<->completed, pending<->failed, failed->completed.
 * `notes` is required (1-500 chars). admin_user_id is injected by the backend proxy.
 */
export async function updatePaymentStatus(
  paymentId: string,
  data: { new_status: 'pending' | 'completed' | 'failed'; notes: string },
  storeId?: number
): Promise<PaymentStatusUpdateResponse> {
  const query = storeId != null ? `?store_id=${storeId}` : '';
  return fetchWithAuth(`${API_BASE_URL}/payments/${paymentId}/status${query}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}
